import React, { useMemo, useState } from "react";
import { Avatar, Box, Card, CardContent, CardHeader, Divider, Tab, Tabs, Typography } from "@mui/material";

function studentAvatar(student) {
    return student.avatar_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(student.name || "Student")}&background=6366f1&color=fff&size=64`;
}

function scoreBadgeColor(percent) {
    if (percent >= 75) return "success.main";
    if (percent >= 40) return "warning.main";
    return "error.main";
}

export default function StudentMatrixCard({ matrix }) {
    const [tab, setTab] = useState(0);
    const students = matrix?.students || []; // eslint-disable-line react-hooks/exhaustive-deps
    const lessons = matrix?.past_lessons || []; // eslint-disable-line react-hooks/exhaustive-deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const homeworkMap = matrix?.homework_map || {};
    const attendanceMap = matrix?.attendance_map || {};
    const assignmentHeaders = matrix?.assignment_headers || [];
    const assignmentMap = matrix?.assignment_map || {};

    const homeworkMissingLessonIds = useMemo(() => {
        const missing = {};
        lessons.forEach((lesson) => {
            const lessonId = Number(lesson.id);
            let hasHomework = false;
            students.forEach((student) => {
                const cell = homeworkMap[`${lessonId}_${Number(student.student_id)}`] || { state: "no_homework" };
                if (cell.state !== "no_homework") hasHomework = true;
            });
            if (!hasHomework) missing[lessonId] = true;
        });
        return missing;
    }, [lessons, students, homeworkMap]);

    return (
        <Card variant="outlined" sx={{ minWidth: 0 }}>
            <CardHeader
                title="Student Matrix"
                action={
                    <Tabs value={tab} onChange={(_, next) => setTab(next)} sx={{ minHeight: 36 }}>
                        <Tab label="Homeworks" sx={{ minHeight: 36 }} />
                        <Tab label="Attendance" sx={{ minHeight: 36 }} />
                        <Tab label="Assignments" sx={{ minHeight: 36 }} />
                    </Tabs>
                }
            />
            <Divider />
            <CardContent sx={{ overflowX: "auto" }}>
                {students.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">No students in this classroom.</Typography>
                ) : tab === 0 ? (
                    <Box component="table" sx={{ width: "100%", borderCollapse: "collapse", "& th, & td": { border: "1px solid", borderColor: "divider", p: 1, textAlign: "center", whiteSpace: "nowrap" } }}>
                        <thead><tr><th style={{ minWidth: 220, textAlign: "left" }}>Student</th>{lessons.map((lesson) => <th key={lesson.id} style={{ minWidth: 110, background: homeworkMissingLessonIds[lesson.id] ? "rgba(237, 108, 2, 0.12)" : undefined }}>L#{lesson.id}</th>)}</tr></thead>
                        <tbody>
                            {students.map((student) => (
                                <tr key={student.student_id}>
                                    <td style={{ textAlign: "left" }}>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                            <Avatar src={studentAvatar(student)} sx={{ width: 28, height: 28 }} />
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{student.name}</Typography>
                                        </Box>
                                    </td>
                                    {lessons.map((lesson) => {
                                        const key = `${Number(lesson.id)}_${Number(student.student_id)}`;
                                        const cell = homeworkMap[key] || { state: "no_homework", is_done: false };
                                        return (
                                            <td key={key} style={{ background: homeworkMissingLessonIds[lesson.id] ? "rgba(237, 108, 2, 0.12)" : undefined }}>
                                                {cell.state === "no_homework" ? "--" : cell.state === "no_submission" ? "No submission" : cell.is_done ? "Reviewed" : "Submitted"}
                                            </td>
                                        );
                                    })}
                                </tr>
                            ))}
                        </tbody>
                    </Box>
                ) : tab === 1 ? (
                    <Box component="table" sx={{ width: "100%", borderCollapse: "collapse", "& th, & td": { border: "1px solid", borderColor: "divider", p: 1, textAlign: "center", whiteSpace: "nowrap" } }}>
                        <thead><tr><th style={{ minWidth: 220, textAlign: "left" }}>Student</th>{lessons.map((lesson) => <th key={lesson.id} style={{ minWidth: 110 }}>L#{lesson.id}</th>)}</tr></thead>
                        <tbody>
                            {students.map((student) => (
                                <tr key={student.student_id}>
                                    <td style={{ textAlign: "left" }}>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                            <Avatar src={studentAvatar(student)} sx={{ width: 28, height: 28 }} />
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{student.name}</Typography>
                                        </Box>
                                    </td>
                                    {lessons.map((lesson) => {
                                        const key = `${Number(lesson.id)}_${Number(student.student_id)}`;
                                        const known = Object.prototype.hasOwnProperty.call(attendanceMap, key);
                                        const value = known ? !!attendanceMap[key] : null;
                                        return <td key={key}>{value === null ? "⚠" : value ? "✓" : "✕"}</td>;
                                    })}
                                </tr>
                            ))}
                        </tbody>
                    </Box>
                ) : (
                    <Box component="table" sx={{ width: "100%", borderCollapse: "collapse", "& th, & td": { border: "1px solid", borderColor: "divider", p: 1, textAlign: "center", whiteSpace: "nowrap" } }}>
                        <thead><tr><th style={{ minWidth: 220, textAlign: "left" }}>Student</th>{assignmentHeaders.map((assignment) => <th key={assignment.id} style={{ minWidth: 140 }}>A#{assignment.id}</th>)}</tr></thead>
                        <tbody>
                            {students.map((student) => (
                                <tr key={student.student_id}>
                                    <td style={{ textAlign: "left" }}>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                            <Avatar src={studentAvatar(student)} sx={{ width: 28, height: 28 }} />
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{student.name}</Typography>
                                        </Box>
                                    </td>
                                    {assignmentHeaders.map((assignment) => {
                                        const key = `${Number(assignment.id)}_${Number(student.student_id)}`;
                                        const cell = assignmentMap[key] || { score_value: null, status: "not_scored" };
                                        const max = Number(assignment.max_value || 0);
                                        const score = cell.score_value == null ? null : Number(cell.score_value);
                                        const percent = score != null && max > 0 ? Math.max(0, Math.min(100, Math.round((score / max) * 100))) : 0;
                                        return (
                                            <td key={key}>
                                                <Typography variant="caption" sx={{ fontWeight: 600 }}>{score == null ? "—" : score} / {max}</Typography>
                                                <Box sx={{ mt: 0.5, height: 6, borderRadius: 10, bgcolor: "action.hover", overflow: "hidden" }}>
                                                    <Box sx={{ width: `${score == null ? 0 : percent}%`, height: "100%", bgcolor: score == null ? "text.disabled" : scoreBadgeColor(percent) }} />
                                                </Box>
                                            </td>
                                        );
                                    })}
                                </tr>
                            ))}
                        </tbody>
                    </Box>
                )}
            </CardContent>
        </Card>
    );
}

